import axios from 'axios'
import router from '@/router'
import store from '@/store'
import ApiCacheService from '@/services/ApiCacheService'

const config = {
    baseURL: `${process.env.VUE_APP_ROOT_API}`,
    withCredentials: false, // This is the default
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
    },
}
let originalUrl = null

const apiClient = axios.create(config)

function getCacheSession() {
    return store.state.cacheSession || store.state.user?.id || 'anonymous'
}

function cacheKey(resource) {
    return `v1:${getCacheSession()}:${resource}`
}

function mergeLicence(existing, licence) {
    if (!existing) {
        return licence
    }

    const merged = {...existing, ...licence}

    if (licence.owner === undefined || typeof licence.owner === 'string') {
        merged.owner = existing.owner
    }
    if (licence.product === undefined || typeof licence.product === 'string') {
        merged.product = existing.product
    }
    if (licence.partner === undefined || typeof licence.partner === 'string') {
        merged.partner = existing.partner
    }

    return merged
}

function shouldInsertLicence(key, licence) {
    if (key === cacheKey('licences')) {
        return true
    }

    const ownerId = licence?.owner?.id || licence?.owner
    return !!ownerId && key === cacheKey(`users:${ownerId}:licences`)
}

async function updateCacheItem(key, item, options = {}) {
    if (!item?.id) {
        return
    }

    await ApiCacheService.updateArrayItem(
        key,
        item.id,
        existing => existing ? {...existing, ...item} : item,
        options
    )
}

async function removeCacheItem(key, item) {
    if (!item?.id) {
        return
    }

    await ApiCacheService.removeArrayItem(key, item.id)
}

async function updateTaggedCollectionCaches(resource, item) {
    if (!item?.id) {
        return
    }

    const keys = await ApiCacheService.keys()
    const prefix = cacheKey(resource)
    await Promise.all(keys
        .filter(key => key.startsWith(prefix))
        .map(key => updateCacheItem(key, item, {insert: key === prefix}))
    )
}

async function removeFromTaggedCollectionCaches(resource, item) {
    if (!item?.id) {
        return
    }

    const keys = await ApiCacheService.keys()
    const prefix = cacheKey(resource)
    await Promise.all(keys
        .filter(key => key.startsWith(prefix))
        .map(key => removeCacheItem(key, item))
    )
}

async function updateLicenceInCache(licence) {
    if (!licence?.id) {
        return
    }

    const keys = await ApiCacheService.keys()
    const prefix = cacheKey('')
    await Promise.all(keys
        .filter(key => key.startsWith(prefix) && key.includes('licences'))
        .map(key => ApiCacheService.updateArrayItem(
            key,
            licence.id,
            existing => mergeLicence(existing, licence),
            {insert: shouldInsertLicence(key, licence)}
        ))
    )
}

async function removeLicenceFromCache(licence) {
    if (!licence?.id) {
        return
    }

    const keys = await ApiCacheService.keys()
    const prefix = cacheKey('')
    await Promise.all(keys
        .filter(key => key.startsWith(prefix) && key.includes('licences'))
        .map(key => ApiCacheService.removeArrayItem(key, licence.id))
    )
}

function authHeaderInterceptor(config) {
    const token = store.state?.user?.tokens?.access?.token
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
}

function accessTokenRefreshInterceptor(error) {
    const status = error.response?.status
    const config = error.config
    if (status === 401 && store.state.user?.supportLogin) {
        store.commit('logout')
        router.replace('/support-login').catch(() => {})
        return Promise.reject(error)
    }
    if(config.url !== '/auth/refresh-tokens') {
        originalUrl = config.url
    }
    if (status !== 401) {
        return Promise.reject(error);
    }
    if (config.url === '/auth/login'
        || config.url === '/sso/authTokens') {
        return Promise.reject(error);
    }

    // TODO check if user is disabled
    if (config.url === '/auth/refresh-tokens') {
        store.commit('logout')
        if(originalUrl !== '/sso/authCode') {
            router.push({ name: 'adminLogin'});
        }
        return Promise.reject(error);
    }
    const refreshToken = store.getters.refreshToken ? store.getters.refreshToken : 'invalid_token'
    if(!config._retry) {
        return apiClient.post('/auth/refresh-tokens', { refreshToken }).then(response => {
            const tokens = response?.data
            store.commit('updateTokens', tokens)
            config.headers['Authorization'] = `Bearer ${tokens?.access?.token}`;
            config._retry = true
            return apiClient.request(config)
        })
    }
}

apiClient.interceptors.request.use((config) => {
    return authHeaderInterceptor(config)
})
apiClient.interceptors.response.use(response => {
    return response
}, error => {
    return accessTokenRefreshInterceptor(error)
})
export default {
    createSupportLogin(userId) {
        return apiClient.post('/auth/support-login', { userId }).then(response => response.data)
    },
    login(email, password) {
        return apiClient.post('/auth/login', { email, password }).then(response => {
                return response.data
            }
        )
    },
    logout(refreshToken) {
        return apiClient.post('/auth/logout', { refreshToken }).catch((error) => {
            if (error.response && error.response.status !== 404) {
                throw error;
            }
        })
    },
    refreshToken(refreshToken) {
        return apiClient.post('/auth/refresh-tokens', { refreshToken }).then(response => {
            return response?.data
        })
    },
    getBadge(badgeId) {
        return apiClient.get( `/badges/${badgeId}`).then(result => {
            return result.data
        })
    },
    getBadges(options = {}) {
        return ApiCacheService.remember(cacheKey('badges'), () => {
            return apiClient.get( "/badges").then(result => {
                return result.data.results
            })
        }, options)
    },
    saveBadge(badge) {
        const _badge = {...badge}
        _badge.image =   badge.image?.id
        delete _badge.author
        delete _badge.lastUpdatedBy
        if(!badge.id) {
            return apiClient.post( "/badges", _badge).then(result => {
                return result.data.results
            }).then(savedBadge => {
                return updateCacheItem(cacheKey('badges'), savedBadge, {insert: true}).then(() => savedBadge)
            })
        } else {
            delete _badge.id
            delete _badge.createdAt
            delete _badge.updatedAt
            return apiClient.patch( `/badges/${badge.id}`, _badge).then(result => {
                return result.data.results
            }).then(savedBadge => {
                const mergedBadge = {...badge, ...savedBadge}
                return updateCacheItem(cacheKey('badges'), mergedBadge).then(() => mergedBadge)
            })
        }
    },
    deleteBadge(badge) {
        return apiClient.delete( `/badges/${badge.id}`).then(result => {
            return removeCacheItem(cacheKey('badges'), badge).then(() => result)
        })
    },
    getUsers(options = {}) {
        return ApiCacheService.remember(cacheKey('users'), () => {
            return apiClient.get("/users").then(result => {
                return result.data.results
            })
        }, options)
    },
    getUser(id) {
        return apiClient.get("/users/"+id).then(result => {
            return result.data
        })
    },
    getPartners() {
        return apiClient.get("/users?role=partner").then(result => {
            return result.data.results
        })
    },
    getTeamMembers(userId) {
        return apiClient.get(`/users/${userId}/teamMembers`).then(result => {
            return result.data.results
        })
    },
    getManagedUsers(userId) {
        return apiClient.get(`/users/${userId}/managedUsers`).then(result => {
            return result.data.results
        })
    },
    addTeamMember(userId, member) {
        return apiClient.post(`/users/${userId}/teamMembers`, member).then(result => {
            console.log("Employees:", result.data)
        })
    },
    removeTeamMember(userId, memberId) {
        return apiClient.delete(`/users/${userId}/teamMembers/${memberId}`).then(result => result.data.results)
    },
    isEmailTaken(email) {
        return apiClient.get(`/users?email=${email}`).then((result) => {
            return result.data.totalResults > 0
        })
    },
    isProductNameTaken(productName) {
        return apiClient.get(`/products?name=${productName}`).then((result) => {
            return result.data.totalResults > 0
        })
    },
    saveUser(user) {
        const _user = {...user}
        _user.password = _user.password === '' ? undefined : _user.password
        return apiClient.post("/users", _user).then(result => {
            return result.data
        }).then(savedUser => {
            return updateCacheItem(cacheKey('users'), savedUser, {insert: true}).then(() => savedUser)
        })
    },
    updateUser(user) {
        const userId = user.id
        const _user = {...user}
        delete _user.id
        delete _user.assignments
        delete _user.isRegistered
        delete _user.teamMemberships
        delete _user.creator
        delete _user.teamMembers
        delete _user.completeName
        return apiClient.patch(`/users/${userId}`, _user).then(result => {
            return result.data
        }).then(savedUser => {
            const mergedUser = {...user, ...savedUser}
            return updateCacheItem(cacheKey('users'), mergedUser).then(() => mergedUser)
        })
    },
    deleteUser(user) {
        return apiClient.delete(`/users/${user.id}`).then(result => {
            return removeCacheItem(cacheKey('users'), user).then(() => result.data)
        })
    },
    getGroup(group) {
        return apiClient.get(`/groups/${group.id}`).then(result => {
            return result.data.results
        })
    },
    getUserGroups(userId) {
        return apiClient.get(`/users/${userId}/groups`).then(result => {
            return result.data.results
        })
    },
    saveGroup(group) {
        return apiClient.post("/groups", {...group}).then(result => {
            return result.data
        })
    },
    updateGroup(group) {
        const groupId = group.id
        const _group = {...group}
        delete _group.id
        delete _group.owner

        return apiClient.patch(`/groups/${groupId}`, _group).then(result => {
            return result.data
        })
    },
    deleteGroup(group) {
        return apiClient.delete(`/groups/${group}`).then(result => {
            return result.data
        })
    },
    getProducts(options = {}) {
        return ApiCacheService.remember(cacheKey('products'), () => {
            return apiClient.get( "/products").then(result => {
                return result.data.results
            }).catch(error => {
                console.log("Error getting products", error)
                return Promise.reject(error)
            })
        }, options)
    },
    getProduct(product) {
        return apiClient.get( `/products/${product.id}`).then(result => {
            return result.data
        }).catch(error => {
            console.log("Error getting product", error)
            return Promise.reject(error)
        })
    },
    getLicences(options = {}) {
        return ApiCacheService.remember(cacheKey('licences'), () => {
            return apiClient.get( "/licences").then(result => {
                //console.log("Licences:", result.data)
                return result.data.results
            }).catch(error => {
                console.log("Error getting licences", error)
                return Promise.reject(error)
            })
        }, options)
    },
    getUserLicences(userId, options = {}) {
        return ApiCacheService.remember(cacheKey(`users:${userId}:licences`), () => {
            return apiClient.get( `users/${userId}/licences`).then(result => {
                return result.data.results
            }).catch(error => {
                console.log("Error getting licences", error)
                return Promise.reject(error)
            })
        }, options)
    },
    getUserAssignedLicences(userId, options = {}) {
        return ApiCacheService.remember(cacheKey(`users:${userId}:assignedLicences`), () => {
            return apiClient.get( `users/${userId}/assignedLicences`).then(result => {
                return result.data.results
            }).catch(error => {
                console.log("Error getting assigned licences", error)
                return Promise.reject(error)
            })
        }, options)
    },
    saveProduct(product) {
        if(!product.id) {
            return apiClient.post("/products", product).then(result => {
                return result.data
            }).then(savedProduct => {
                return updateCacheItem(cacheKey('products'), savedProduct, {insert: true}).then(() => savedProduct)
            })
        } else {
            const _product = {...product}
            const productId = product.id
            delete _product.id
            delete _product.createdAt
            delete _product.updatedAt
            delete _product.lastUpdatedBy
            delete _product.author
            return apiClient.patch(`/products/${productId}`, _product).then(result => {
                return result.data
            }).then(savedProduct => {
                const mergedProduct = {...product, ...savedProduct}
                return updateCacheItem(cacheKey('products'), mergedProduct).then(() => mergedProduct)
            })
        }
    },
    saveLicence(licence) {
        if(!licence.id){
            return apiClient.post("/licences", licence).then(result => {
                return result.data
            }).then(savedLicence => {
                return updateLicenceInCache(savedLicence).then(() => savedLicence)
            })
        } else {
            const {startDate, expirationDate, quantityTotal, orderNumber, comment, partner, isFrontendManageable, logging} =  licence
            return apiClient.patch(`/licences/${ licence.id }`, {
                startDate, expirationDate, quantityTotal, orderNumber, comment, partner, isFrontendManageable, logging
            }).then(result => {
                return result.data
            }).then(savedLicence => {
                const mergedLicence = {...licence, ...savedLicence}
                return updateLicenceInCache(mergedLicence).then(() => mergedLicence)
            })
        }
    },
    updateCachedLicence(licence) {
        return updateLicenceInCache(licence)
    },
    updateAssignments(licenceId, assignments) {
        return apiClient.put(`/licences/${licenceId}/assignments`, {assignments} ).then(result => {
            console.log(result)
        })
    },
    getAssignments(licenceId) {
        return apiClient.get(`/licences/${licenceId}/assignments`).then(result => result.data)
    },
    getAssignedCourseUsers(courseId) {
        return apiClient.get(`courses/${courseId}/assignedUsers`).then(result => result.data.results)
    },
    deleteLicence(licence) {
        return apiClient.delete(`/licences/${licence.id}`).then(result => {
            return removeLicenceFromCache(licence).then(() => result)
        })
    },
    deleteProduct(product) {
        return apiClient.delete(`/products/${product.id}`).then(result => {
            return removeCacheItem(cacheKey('products'), product).then(() => result)
        })
    },
    getAuthCode(clientId, redirectUri, scope) {
        return apiClient.post(`/sso/authCode`, {clientId, redirectUri, scope}).then(result => {
            return result.data
        })
    },
    getCourses(tags = undefined, options = {}) {
        return ApiCacheService.remember(cacheKey(`courses${tags ? `:${tags}` : ''}`), () => {
            return apiClient.get( "/courses" + (tags ? "?tags=" + tags  : "")).then(result => {
                return result.data.results
            })
        }, options)
    },
    getUserCourses(userId) {
        return apiClient.get( `/users/${userId}/courses`).then(result => {
            return result.data
        })
    },
    getCoursesForLicence(licence) {
        return apiClient.get( `/products/${licence.product.id}/courses?populateCourses=true`).then(result => {
            return result.data.courses
        })
    },

    deleteCourse(course) {
        return apiClient.delete(`/courses/${course.id}`).then(result => {
            return removeFromTaggedCollectionCaches('courses', course).then(() => result)
        })
    },
    saveCourse(course) {
        const _course = { ...course }
        _course.image = _course.image ? _course.image.id : undefined
        _course.events.forEach(event => delete event['id'])
        if(!course.id) {
            return apiClient.post("/courses", _course).then(result => {
                return result.data
            }).then(savedCourse => {
                return updateTaggedCollectionCaches('courses', savedCourse).then(() => savedCourse)
            })
        } else {
            const courseId = course.id
            delete _course.id
            delete _course.createdAt
            delete _course.updatedAt
            delete _course.lastUpdatedBy
            delete _course.author
            return apiClient.patch(`/courses/${courseId}`, _course).then(result => {
                return result.data
            }).then(savedCourse => {
                const mergedCourse = {...course, ...savedCourse}
                return updateTaggedCollectionCaches('courses', mergedCourse).then(() => mergedCourse)
            })
        }
    },
    getCourse(course, deep = false) {
        return apiClient.get(`/courses/${course.id}?deep=${deep ? "true": "false"}`).then(result => {
            return result.data
        })
    },
    isCourseTitleTaken(courseTitle) {
        return apiClient.get(`/courses?name=${courseTitle}`).then((result) => {
            return result.data.totalResults > 0
        })
    },
    upload(file, onUploadProgress, extractZip, description = "") {
        let formData = new FormData();

        formData.append("file", file);
        formData.append("description", description)
        formData.append("unzipContent", extractZip.toString())

        return apiClient.post("/content/", formData, {
            headers: {
                "Content-Type": "multipart/form-data"
            },
            onUploadProgress,
            timeout: 0
        });
    },
    getChapters(tags = undefined, options = {}) {
        return ApiCacheService.remember(cacheKey(`chapters${tags ? `:${tags}` : ''}`), () => {
            return apiClient.get( "/chapters" + (tags ? "?tags=" + tags  : "")).then(result => {
                return result.data.results
            })
        }, options)
    },
    getTags() {
        return apiClient.get( "/tags").then(result => result.data.results).then(tags => {
            //console.log("Tags:", tags.data)
            return tags
        })
    },
    deleteChapter(chapter) {
        return apiClient.delete(`/chapters/${chapter.id}`).then(result => {
            return removeFromTaggedCollectionCaches('chapters', chapter).then(() => result)
        })
    },
    saveChapter(chapter) {
        const _chapter = { ...chapter }
        if(chapter.content) {
            _chapter.content=_chapter.content.map(content=> ({file: content.file.id, path: content.path}))
        }
        _chapter.image = _chapter.image ? _chapter.image.id : undefined
        if(!chapter.id) {
            return apiClient.post("/chapters", _chapter).then(result => {
                return result.data
            }).then(savedChapter => {
                return updateTaggedCollectionCaches('chapters', savedChapter).then(() => savedChapter)
            })
        } else {
            const chapterId = chapter.id
            delete _chapter.id
            delete _chapter.createdAt
            delete _chapter.updatedAt
            delete _chapter.assignedTo
            delete _chapter.lastUpdatedBy
            delete _chapter.author
            return apiClient.patch(`/chapters/${chapterId}`, _chapter).then(result => {
                return result.data
            }).then(savedChapter => {
                const mergedChapter = {...chapter, ...savedChapter}
                return updateTaggedCollectionCaches('chapters', mergedChapter).then(() => mergedChapter)
            })
        }
    },
    getChapter(chapter) {
        return apiClient.get(`/chapters/${chapter.id}`).then(result => {
            return result.data
        })
    },
    deleteLesson(lesson) {
        return apiClient.delete(`/lessons/${lesson.id}`).then(result => {
            return removeFromTaggedCollectionCaches('lessons', lesson).then(() => result)
        })
    },
    getLesson(lesson) {
        return apiClient.get(`/lessons/${lesson.id}`).then(result => {
            return result.data
        })
    },
    getLessons(tags = undefined, options = {}) {
        return ApiCacheService.remember(cacheKey(`lessons${tags ? `:${tags}` : ''}`), () => {
            return apiClient.get( "/lessons" + (tags ? "?tags=" + tags  : "")).then(result => {
                return result.data.results
            })
        }, options)
    },
    saveLesson(lesson) {
        const _lesson = { ...lesson }
        if(lesson.content) {
            _lesson.content=_lesson.content.map(content=> ({file: content.file.id, path: content.path}))
        }
        _lesson.image = _lesson.image ? _lesson.image.id : undefined
        if(!lesson.id) {
            return apiClient.post("/lessons", _lesson).then(result => {
                return result.data
            }).then(savedLesson => {
                return updateTaggedCollectionCaches('lessons', savedLesson).then(() => savedLesson)
            })
        } else {
            const lessonId = lesson.id
            delete _lesson.id
            delete _lesson.createdAt
            delete _lesson.updatedAt
            delete _lesson.assignedTo
            delete _lesson.lastUpdatedBy
            delete _lesson.author
            return apiClient.patch(`/lessons/${lessonId}`, _lesson).then(result => {
                return result.data
            }).then(savedLesson => {
                const mergedLesson = {...lesson, ...savedLesson}
                return updateTaggedCollectionCaches('lessons', mergedLesson).then(() => mergedLesson)
            })
        }
    },
    isChapterTitleTaken(chapterTitle) {
        return apiClient.get(`/chapters?name=${chapterTitle}`).then((result) => {
            return result.data.totalResults > 0
        })
    },
    getFiles() {
        return apiClient.get("/files");
    },
    /* Events
    *  Event Types
    *  curl -X GET -H 'Content-type:application/json' -H 'Authorization: Bearer xxx' 'https://api.c-learning.de:444/v1/statistics/logs?fromDate=2022-01-01&toDate=2023-12-31&userId=62f9f8cb11b882002710eecc'
    *  Man kann jetzt 'type', 'userId', 'courseId', 'chapterId', 'lessonId' sowie fromDate und toDate angeben.
    *
    * */
    sendEvent(type, value = "", courseId = "", chapterId = "", lessonId = "", licenseId = ""){


        let data = {
            "type": type
        };
        if(value !== ""){
            data.value = value;
        }
        if(courseId !== ""){
            data.courseId = courseId;
        }
        if(chapterId !== ""){
            data.chapterId = chapterId
        }
        if(lessonId !== ""){
            data.lessonId = lessonId;
        }
        if(licenseId !== ""){
            data.licenceId = licenseId;
        }

        return apiClient.post("/statistics/logs", data).then(result => {
            //console.log(result);
            return result.data
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    getEvents(type = "", userId = "", courseId = "", chapterId = "", lessonId = "", value = "", fromDate = null, toDate = null, licenseId = ""){

        let addParams = "";
        if(type !== ""){
            addParams += "&type="+type;
        }
        if(value !== ""){
            addParams += "&value="+value;
        }
        if(courseId !== ""){
            addParams += "&courseId="+courseId;
        }
        if(chapterId !== ""){
            addParams += "&chapterId="+chapterId
        }
        if(lessonId !== ""){
            addParams += "&lessonId="+lessonId;
        }
        if(licenseId !== ""){
            addParams += "&licenceId="+licenseId;
        }
        if(userId !== ""){
            addParams += "&userId="+userId;
        }
        if(fromDate){
            addParams += "&fromDate="+fromDate.toISOString().slice(0, 10);
        }
        if(toDate){
            addParams += "&toDate="+toDate.toISOString().slice(0, 10);
        }


        return apiClient.get(`/statistics/logs/?limit=10000000&sortBy=date:desc`+addParams).then(result => {
            return result.data.results;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    getUserEvents(userId, type = ""){
        let addParams = "";
        if(type != ""){
            addParams += "&type="+type;
        }
        return apiClient.get(`/statistics/logs/?userId=${userId}&sortBy=date:desc`+addParams).then(result => {
            return result.data;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    getStudyProgress(courseId, fromDate, toDate = new Date()){
        let addParams = "?sortBy=completionDate:desc";
        if(fromDate){
            addParams += "&fromDate="+fromDate.toISOString().slice(0, 10);
        }
        if(toDate){
            addParams += "&toDate="+toDate.toISOString().slice(0, 10);
        }
        return apiClient.get("/courses/"+courseId+"/studyProgress"+addParams).then(result => {
            return result.data.results;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    getStudyProgressUser(userId, courseId = null, fromDate = null, toDate = null){
        if (courseId instanceof Date && fromDate instanceof Date && !toDate) {
            toDate = fromDate;
            fromDate = courseId;
            courseId = null;
        }

        const dateParam = (date) => date instanceof Date ? date.toISOString().slice(0, 10) : date;
        let params = [];
        if(courseId){
            params.push("course="+courseId);
        }
        if(fromDate){
            params.push("fromDate="+dateParam(fromDate));
        }
        if(toDate){
            params.push("toDate="+dateParam(toDate));
        }
        let addParams = params.length ? "?"+params.join("&") : "";
        return apiClient.get("/users/"+userId+"/studyProgress"+addParams).then(result => {
            let sortedResult = result.data.results.sort((a,b) => {
                let dateA = a.completionDate ? new Date(a.completionDate) : new Date(0); // epoch if no date
                let dateB = b.completionDate ? new Date(b.completionDate) : new Date(0); // epoch if no date
                return dateB - dateA;
            });
            return sortedResult;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
     importUsers(userId, selectedImportUsers) {
        const importUsers = selectedImportUsers.map((user) => ({...user, invalid: undefined, password: user.password ? user.password: undefined}))
        return apiClient.post(`/users/${userId}/import`, importUsers).then(result => {
            return result.data
        })
    },
    migrateUsers(selectedImportUsers) {
        return apiClient.post("/users/migrate", selectedImportUsers).then(result => {
            return result.data
        })
    },
    //Favorites
    async createFavorite(favorite) {
        return apiClient.post("/favorites", favorite).then(result => {
            return result.data
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async updateFavorite(favorite) {
        return apiClient.put("/favorites/"+favorite.id, favorite).then(result => {
            return result.data
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async deleteFavorite(favorite_id){
        return apiClient.delete("/favorites/"+favorite_id).then(result => {
            return result.data
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async getFavorites(list) {
        return apiClient.get('/favorites?list='+list).then(result => {
            return result.data.results
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async getFavoritesForUser(user) {
        return apiClient.get('/favorites?user='+user).then(result => {
            return result.data.results
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async createFavoritesLists(list) {
        return apiClient.post("/favoriteslists", list).then(result => {
            return result.data
        }).catch(error => {
            console.log("createFAvoritesLists error: "+error);
            return null;
        });
    },
    async updateFavoritesLists(list) {
        let _list = {...list};
        delete _list.id;
        return apiClient.patch("/favoriteslists/"+list.id, _list).then(result => {
            return result.data
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async deleteFavoritesList(list_id){
        return apiClient.delete("/favoriteslists/"+list_id).then(result => {
            return result.data
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async getFavoritesLists() {
        return apiClient.get("/favoritesLists").then(result => {
            return result.data.results;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async getFavoritesListsUser(userId = null) {
        let params = "";
        if (userId) {
            params = "?user=" + userId;
        }
        return apiClient.get("/favoritesLists" + params).then(result => {
            let favs = [];
            for(let i=0; i < result.data.results.length; i++){
                if(!result.data.results[i].group || result.data.results[i].group == ""){
                    favs.push(result.data.results[i]);
                }
            }
            return favs;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async getFavoritesList(listId) {
        return apiClient.get("/favoritesLists/" + listId).then(result => {
            return result.data;
        }).catch(error => {
            console.log(error);
            return null;
        });
    },
    async getFavoritesListsGroup(group) {
        return apiClient.get('/favoriteslists?group='+group).then(async result => {
            return result.data.results
        }).catch(error => {
            console.log(error);
            return false;
        });
    },
    getCourseUsers(course_id){
        return apiClient.get(`/courses/${course_id}/assignedUsers`).then(result => {
            //console.log(result.data);
            return result.data.results;
        })
    },
}
